/**
 * Every practice's four buckets — the view §3 of the design document asks for by name:
 * *"The tenant dashboard shows all four… Practitioners will ask 'where is my money'
 * constantly if you only show one number."* The super admin needs the same four to answer
 * them, and had no screen at all.
 *
 * Fed from `GET /liability`, which already scans every balance document server-side and
 * returns both the per-currency totals and every tenant row — so this screen and the treasury
 * strip read the SAME snapshot rather than two that can disagree.
 *
 * `?debt=1` deep-links here from the overview's debt alarm and pre-filters to the practices
 * that owe the platform money.
 */
import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { ALARM_ROW, COLHEAD, Criteria, Figure, IDENT, MUTED, RULE } from "./Ledger";
import { Freshness, ResourceState, useGatewayResource } from "./ResourceState";
import { liabilityTenantToBuckets } from "./shapes";

const FILTERS = ["all", "debt", "frozen"];

export default function BalancesScreen({ t, gatewayApi }) {
  const [params, setParams] = useSearchParams();
  const filter = params.get("debt") === "1" ? "debt"
    : FILTERS.includes(params.get("filter")) ? params.get("filter") : "all";

  const fetcher = useCallback(() => gatewayApi.adminLiability(), [gatewayApi]);
  const resource = useGatewayResource(fetcher);

  const rows = useMemo(() => {
    const all = (resource.data?.tenants || []).map(liabilityTenantToBuckets);
    if (filter === "debt") return all.filter((r) => (r.debtCents || 0) > 0);
    if (filter === "frozen") return all.filter((r) => r.frozen);
    return all;
  }, [resource.data, filter]);

  const setFilter = (next) => {
    const p = { screen: "balances" };
    if (next !== "all") p.filter = next;
    setParams(p, { replace: false });
  };

  return (
    <div data-testid="balances-screen">
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <Criteria>{t("gw.console.balances.criteria")}</Criteria>
        <Freshness resource={resource} id="balances" />
      </div>

      <div className="flex items-center gap-4 mb-3 text-xs">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
                  aria-current={filter === f ? "page" : undefined}
                  className={filter === f
                    ? "text-slate-900 font-medium underline underline-offset-4"
                    : "text-slate-400 hover:text-slate-700"}
                  data-testid={`balances-filter-${f}`}>
            {t(`gw.console.balances.filter.${f}`)}
          </button>
        ))}
      </div>

      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr className={RULE}>
            <th className={`${COLHEAD} py-2`}>{t("gw.console.balances.colTenant")}</th>
            <th className={`${COLHEAD} py-2`}>{t("gw.console.treasury.colCurrency")}</th>
            <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.treasury.colOnHold")}</th>
            <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.treasury.colReady")}</th>
            <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.treasury.colInTransit")}</th>
            <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.treasury.colOwed")}</th>
          </tr>
        </thead>
        <ResourceState
          resource={resource} id="balances" as="tbody" colSpan={6}
          emptyText={t(filter === "all" ? "gw.console.balances.empty"
                                       : `gw.console.balances.emptyFiltered.${filter}`)}
          isEmpty={() => rows.length === 0}>
          {() => rows.map((r) => (
            <React.Fragment key={`${r.tenantId}:${r.currency}`}>
              <tr className={RULE} data-testid={`balance-row-${r.tenantId}-${r.currency}`}>
                <td className={`py-2 ${IDENT}`}>{r.tenantId}</td>
                <td className="py-2 uppercase text-slate-700">{r.currency}</td>
                <td className="py-2"><Figure cents={r.onHoldCents} currency={r.currency} /></td>
                <td className="py-2"><Figure cents={r.readyCents} currency={r.currency} /></td>
                <td className="py-2"><Figure cents={r.inTransitCents} currency={r.currency} muted /></td>
                <td className="py-2"><Figure cents={r.owedCents} currency={r.currency} /></td>
              </tr>
              {((r.debtCents || 0) > 0 || r.frozen) && (
                <tr data-testid={`balance-flag-${r.tenantId}-${r.currency}`}>
                  <td colSpan={6} className="pb-2">
                    <span className={`${ALARM_ROW} text-xs`} role="alert">
                      {r.frozen
                        ? t("gw.console.balances.frozen")
                        : t("gw.console.balances.inDebt")}
                    </span>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </ResourceState>
      </table>

      <p className={`${MUTED} mt-3`}>{t("gw.console.treasury.noCrossCurrency")}</p>
    </div>
  );
}
